import { useEffect, useMemo, useState, type CSSProperties, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  kstDateTime,
  SERVICE_ORDER_STATUS_LABELS,
  type CustomerSheetReportDto,
} from '@bhops/shared';
import { customerSheetReportsApi } from '@/api/customerSheetReports';
import { visitLabel } from '@/lib/report-visit';
import { pill, SHEET_STATUS_TONE, SHEET_STATUS_TONE_UNKNOWN } from '@/pages/field/field-tones';

// 2026-11-03 오너 — "앱을 통해 리포트가 넘어와서 고객사 리포트에 등록이 됐다 쳐. 그런데 필드팀 방문 필요
// 건이여서 필드팀이 캘린더에 방문 일정을 만들어. 그러면 중복된 리포트가 등록이 되는데, 리포트 넘버는 2개가
// 되잖아".
//
// 유지보수 일정을 추가할 때 그 고객사의 처리 안 된 리포트를 먼저 보여 주고, '기존 리포트에 잇기'와
// '새 리포트로 등록' 중 하나를 꼭 고르게 한다. 후보가 없으면 묻지 않고 새 리포트로 등록한다.
// 매장명은 손으로 적는 글이라 후보에 안 걸린 리포트는 검색으로 찾는다.

/** '' = 아직 안 고름, 'new' = 새 리포트, 그 밖 = 이을 리포트 id. */
export type ReportChoice = '' | 'new' | (string & {});

export function MaintenanceReportChoice({
  customerId,
  value,
  onChange,
}: {
  customerId: string;
  value: ReportChoice;
  onChange: (v: ReportChoice) => void;
}) {
  const [search, setSearch] = useState('');
  const [applied, setApplied] = useState('');

  const candQ = useQuery({
    queryKey: ['new-schedule', 'report-candidates', customerId],
    queryFn: () => customerSheetReportsApi.candidates(customerId),
  });
  const searchQ = useQuery({
    queryKey: ['new-schedule', 'report-search', applied],
    queryFn: () => customerSheetReportsApi.list({ search: applied, open: true, pageSize: 20 }),
    enabled: applied.length > 0,
  });

  const candidates = useMemo(() => candQ.data?.items ?? [], [candQ.data]);
  const found = useMemo(() => {
    const seen = new Set(candidates.map((c) => c.id));
    return (searchQ.data?.items ?? []).filter((r) => !seen.has(r.id));
  }, [candidates, searchQ.data]);

  // 후보가 없으면(또는 못 불러오면) 물을 것이 없다 — 새 리포트로.
  const nothingToLink = candQ.isError || (candQ.isSuccess && candidates.length === 0);
  useEffect(() => {
    if (nothingToLink && value === '') onChange('new');
  }, [nothingToLink, value, onChange]);

  if (candQ.isLoading) {
    return (
      <div style={field}>
        <span style={labelSt}>고객사 리포트</span>
        <span style={hint}>이 고객사의 처리 안 된 리포트를 찾는 중…</span>
      </div>
    );
  }

  const searchForm = (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        setApplied(search.trim());
      }}
      style={{ display: 'flex', gap: 6 }}
    >
      <input
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="리포트 번호나 매장명으로 찾기"
        aria-label="이을 리포트 찾기"
        style={searchInput}
        data-testid="new-schedule-report-search"
      />
      <button type="submit" style={searchBtn}>
        찾기
      </button>
    </form>
  );

  if (nothingToLink && found.length === 0) {
    return (
      <div style={field} data-testid="new-schedule-report-none">
        <span style={labelSt}>고객사 리포트</span>
        <span style={hint}>
          {candQ.isError
            ? '처리 안 된 리포트를 불러오지 못해 새 리포트로 등록합니다.'
            : '이 고객사의 처리 안 된 리포트가 없어 새 리포트로 등록합니다.'}{' '}
          매장명이 달라 안 보이는 리포트가 있으면 찾아서 이을 수 있습니다.
        </span>
        {searchForm}
        {applied && searchQ.isSuccess && <span style={hint}>맞는 리포트가 없습니다.</span>}
      </div>
    );
  }

  return (
    <div style={field} role="radiogroup" aria-label="고객사 리포트">
      <span style={labelSt}>
        고객사 리포트{' '}
        <span style={{ fontWeight: 400, color: 'var(--fg-muted)' }}>
          {value === '' ? '하나를 골라 주세요' : null}
        </span>
      </span>
      <span style={hint}>
        이 고객사에 처리 안 된 리포트가 있습니다. 같은 건이면 그 리포트에 이어 번호를 하나로 둡니다.
      </span>
      <div style={list}>
        <Option
          checked={value === 'new'}
          onPick={() => onChange('new')}
          testId="new-schedule-report-new"
        >
          <span style={{ fontWeight: 600 }}>새 리포트로 등록</span>
          <span style={hint}>다른 건이면 새 번호를 받습니다</span>
        </Option>
        {/* 고를 수 있는 리포트를 위로 — 이미 방문 일정이 이어진 것은 아래로(최근 것부터는 그대로). */}
        {[...candidates, ...found]
          .sort((a, b) => Number(!!a.visit) - Number(!!b.visit))
          .map((r) => (
            <ReportOption
              key={r.id}
              r={r}
              similar={'match' in r && r.match === 'similar'}
              checked={value === r.id}
              onPick={() => onChange(r.id)}
            />
          ))}
      </div>
      {searchForm}
      {applied && searchQ.isSuccess && found.length === 0 && (
        <span style={hint}>위에 없는 리포트 중 맞는 것이 없습니다.</span>
      )}
    </div>
  );
}

function ReportOption({
  r,
  similar,
  checked,
  onPick,
}: {
  r: CustomerSheetReportDto;
  similar: boolean;
  checked: boolean;
  onPick: () => void;
}) {
  const busy = r.visit ?? null;
  return (
    <Option
      checked={checked}
      onPick={onPick}
      disabled={!!busy}
      testId={`new-schedule-report-${r.sheetRowKey}`}
    >
      <span style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 600, color: 'var(--fg-brand)' }}>{r.sheetRowKey}</span>
        <span>{r.storeName?.trim() || '–'}</span>
        <span style={{ ...pill, ...(SHEET_STATUS_TONE[r.status] ?? SHEET_STATUS_TONE_UNKNOWN) }}>
          {SERVICE_ORDER_STATUS_LABELS[r.status]}
        </span>
        {r.fromCalendar && <span style={hint}>캘린더로 만든 리포트</span>}
        {similar && <span style={hint}>매장명이 비슷</span>}
        <span style={{ ...hint, marginLeft: 'auto' }}>{kstDateTime(r.receivedAt) ?? '–'}</span>
      </span>
      <span style={summary}>{r.summary || '요약 없음'}</span>
      {busy && (
        <span style={hint}>
          이미 {visitLabel(busy)}이 이어져 있어 고를 수 없습니다. 그 일정의 날짜를 옮겨 주세요.
        </span>
      )}
    </Option>
  );
}

function Option({
  checked,
  onPick,
  disabled,
  testId,
  children,
}: {
  checked: boolean;
  onPick: () => void;
  disabled?: boolean;
  testId: string;
  children: ReactNode;
}) {
  return (
    <label
      style={{
        ...option,
        background: checked ? 'var(--bg-selected)' : 'transparent',
        opacity: disabled ? 0.55 : 1,
        cursor: disabled ? 'not-allowed' : 'pointer',
      }}
    >
      <input
        type="radio"
        name="new-schedule-report"
        checked={checked}
        disabled={disabled}
        onChange={onPick}
        style={{ marginTop: 3 }}
        data-testid={testId}
      />
      <span style={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1, minWidth: 0 }}>
        {children}
      </span>
    </label>
  );
}

const field: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 6 };
const labelSt: CSSProperties = { fontSize: 13, fontWeight: 600 };
const hint: CSSProperties = { fontSize: 12, color: 'var(--fg-muted)' };
const list: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  maxHeight: 240,
  overflowY: 'auto',
  borderRadius: 'var(--r-input)',
  border: '1px solid var(--border-default)',
};
const option: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 8,
  padding: '8px 10px',
  fontSize: 13,
  borderBottom: '1px solid var(--border-subtle)',
};
const summary: CSSProperties = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  color: 'var(--fg-default)',
};
const searchInput: CSSProperties = {
  flex: 1,
  height: 32,
  padding: '4px 10px',
  borderRadius: 'var(--r-input)',
  border: '1px solid var(--border-default)',
  background: 'var(--bg-surface)',
  color: 'var(--fg-default)',
  fontSize: 13,
};
const searchBtn: CSSProperties = {
  height: 32,
  padding: '0 12px',
  borderRadius: 'var(--r-input)',
  border: '1px solid var(--border-default)',
  background: 'var(--bg-surface)',
  color: 'var(--fg-default)',
  fontSize: 13,
  cursor: 'pointer',
};
